import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import WithModal from "../../cores/WithModal";
import { setNotification } from "@modules/notification";

import {
    getModalFoldersList,
    resetModalSelectedFolder,
    searchModalFolderData,
    toggleNewFolderInModal,
    updateModalFolderSearchState,
    updateModalFolderState,
    updateModalSelectedFolder,
    initModalFolderData,
    createModalDriveFolder,
} from "@modules/folderTreeViewModal";
import FolderTreeView from "@components/tree/folder-tree-view";
import { DRIVE_FOLDER_SEARCH_TYPE } from "../../constants";
import { Grid, Stack, Divider, Button, IconButton, Box } from "@mzc-pdc/ui";
import theme from "@styles/theme";
import { IconFolderAdd, IconRefresh } from "@mzc-cloudplex/icons";
import Tooltip from "@components_v2/Tooltip/tooltip";
import useActionPending from "@hooks/useActionPending";
import AdaptivePopper from "@components_v2/adaptive-popper";
import store from "@store/store";

const SelectFolderPopper = ({
    disablePortal,
    disablePopper,
    domain,
    projectId,
    submitText,
    onSubmit,
    onChangeFolder,
    open,
    placement,
    anchorEl,
    wrapperSx = {},
}) => {
    const { t } = useTranslation();

    const {
        pending: createPending,
        setActionPending: setCreatePending,
        disableActionPending: disableCreatePending,
    } = useActionPending("createFolder");

    const [searchKeyword, setSearchKeyword] = useState("");
    const [selectedFolder, setSelectedFolder] = useState({ id: null, path: "" });
    const [checkClickFolder, setCheckClickFolder] = useState(false);
    const [buttonPending, setButtonPending] = useState(false);
    const [submitPending, setSubmitPending] = useState(false);
    const [checkSubFoldersOption, setCheckSubFoldersOption] = useState(true);

    const { folderList, folderTreeViewModal, driveList, driveId, defaultProjectId } = useSelector(
        ({ folderTreeViewModal, project }) => ({
            folderList: folderTreeViewModal.folderList,
            folderTreeViewModal: folderTreeViewModal,
            driveList: folderTreeViewModal.driveList,
            driveId:
                folderTreeViewModal.driveList?.data?.find((drive) => drive.resourceType?.type === domain)?.id ?? null,
            defaultProjectId: project.id,
        }),
    );

    const dispatch = useDispatch();

    const handleCreateFolder = async (name) => {
        try {
            if (!driveId || createPending) return;
            setCreatePending();
            const params = { name, parent: { id: selectedFolder.id } };
            await dispatch(createModalDriveFolder(driveId, params));

            const queryParameters = {
                parentId: selectedFolder.id,
                searchType: DRIVE_FOLDER_SEARCH_TYPE.DIRECT_CHILDREN,
            };
            const headerParams = { projectId: projectId };
            await dispatch(
                getModalFoldersList({
                    driveId: selectedFolder?.driveId ?? driveId,
                    queryParameters,
                    headerParams,
                }),
            ); //TODO: 드라이브가 여러개일 때 driveId 구할 수 있는 방법을 찾아야함
            dispatch(toggleNewFolderInModal(null));
            dispatch(
                setNotification({
                    type: "success",
                    contents: t(`common::msg::Creation of new folder {{item}} is completed.`, {
                        item: name,
                    }),
                }),
            );
        } catch (error) {
            const errorData = error && error.response && error.response.data;
            const errorMessage = errorData && errorData.message;
            const errorStatus = (error && error.response && error.response.status) || "Unknown";
            const errorContents = `- ${errorMessage}`;

            dispatch(
                setNotification({
                    type: "error",
                    contents:
                        t(`common::label::There was an error creating the new folder {{item}}.`, {
                            item: name,
                        }) + errorContents,
                }),
            );
        } finally {
            dispatch(toggleNewFolderInModal(null));
            disableCreatePending();
        }
    };

    const init = async () => {
        try {
            const headerParams = { projectId: projectId };

            const rootFolder = await dispatch(initModalFolderData({ headerParams, domain }));
            setSelectedFolder({ driveId: rootFolder.driveId, id: rootFolder.id, name: rootFolder.name, path: "" });
            if (onChangeFolder)
                onChangeFolder({
                    driveId: rootFolder.driveId,
                    virtualFolderId: rootFolder.id,
                    virtualFolderName: rootFolder.name,
                });
            dispatch(updateModalSelectedFolder(rootFolder));
            setCheckClickFolder(true);
        } catch (error) {
            console.log(error);
            dispatch(
                setNotification({
                    type: "error",
                    contents: error.response
                        ? `[Error:${error.response.status}] => ${error.response?.data?.message}`
                        : `Internal Error Occured`,
                }),
            );
        }
    };

    const onClickForderTree = async (item, isClickArrow, options = {}) => {
        try {
            // console.log(item);
            setSelectedFolder(item);
            setCheckClickFolder(true);
            if (onChangeFolder)
                onChangeFolder({ driveId: item.driveId, virtualFolderId: item.id, virtualFolderName: item.name });

            //folder-tree-view의 onHandleClickFolder가 마운트 시점 클로저를 잡고 있어 useSelector 스냅샷이 낡을 수 있다
            const { folderList: currentFolderList, isSearch } = store.getState().folderTreeViewModal;
            const currentFolder = currentFolderList.data?.[item.id];
            //트리 행 클릭(toggleFold)도 캐럿과 동일하게 토글한다. 루트 초기화 등 프로그래매틱 호출과
            //검색 결과 트리(매칭 폴더가 이미 펼쳐진 상태로 표시됨)는 펼치기만 한다
            const shouldFold =
                !!currentFolder && (isClickArrow || (options.toggleFold && !isSearch)) && !currentFolder.isFold;

            //폴더 선택은 접힘 여부와 무관하게 수행한다
            if (!isClickArrow) {
                dispatch(updateModalSelectedFolder(item));
            }

            if (!shouldFold) {
                const queryParameters = { parentId: item.id, searchType: DRIVE_FOLDER_SEARCH_TYPE.DIRECT_CHILDREN };
                const headerParams = { projectId: projectId ?? defaultProjectId };

                await dispatch(
                    getModalFoldersList({
                        driveId: item.driveId,
                        queryParameters,
                        headerParams,
                    }),
                );
            } else {
                dispatch(
                    updateModalFolderState({
                        [item.id]: { ...currentFolder, isFold: true },
                    }),
                );
            }
        } catch (error) {
            console.log(error);
        }
    };

    const onClickFolderTreeFilter = async () => {
        try {
            const keyword = searchKeyword;

            if (!keyword) return;

            const queryParameters = {
                name: keyword,
                searchType: DRIVE_FOLDER_SEARCH_TYPE.NAME,
            };

            const headerParams = { projectId: projectId ?? defaultProjectId };
            await dispatch(searchModalFolderData({ headerParams, domain: "Asset", queryParameters }));

            await dispatch(
                updateModalFolderSearchState({
                    isSearch: true,
                }),
            );
        } catch (error) {
            console.log(error);
        }
    };

    const onClickResetFolderTreeFilter = async () => {
        try {
            setSearchKeyword("");

            const headerParams = { projectId: projectId ?? defaultProjectId };
            const rootFolder = await dispatch(initModalFolderData({ headerParams, domain: "Asset" }));
            onClickForderTree(rootFolder, false);
            await dispatch(
                updateModalFolderSearchState({
                    isSearch: false,
                }),
            );
        } catch (error) {
            console.log(error);
        }
    };

    const onClickRefreshFolderTreeFilter = () => {
        if (searchKeyword) {
            onClickFolderTreeFilter();
        } else {
            init();
        }
    };

    const handleClickCreateFolder = (value) => {
        console.log(value);
        if (!value) dispatch(toggleNewFolderInModal(null));
        else handleCreateFolder(value);
    };

    const getFullFolderPath = useMemo(() => {
        let node = selectedFolder;
        let result = [];
        if (!node) return;

        while (node.depth > 0) {
            result = [node, ...result];
            node = folderList.data[node.parent];

            if (node.depth === 0) {
                result = [node, ...result];
            }
        }

        return result;
    }, [selectedFolder, folderList]);

    const onHandleChangeSubFoldersOption = (e) => {
        setCheckSubFoldersOption(e.target.checked);
    };

    const onHandleClickSubmit = useCallback(async () => {
        setSubmitPending(true);
        await onSubmit({
            driveId: selectedFolder.driveId,
            folderId: selectedFolder.id,
            folderName: selectedFolder.name,
            folderPath: getFullFolderPath,
            includeSubFolders: checkSubFoldersOption,
        });
        setSubmitPending(false);
    }, [submitPending, selectedFolder, checkSubFoldersOption]);

    useEffect(() => {
        init();
        return () => dispatch(resetModalSelectedFolder());
    }, []);

    const HeaderComponent = useMemo(
        () => (
            <Box data-role="header" py={1.5} pr={1.5} pl={disablePopper ? 0 : 1.5}>
                <Grid container flexDirection={"row"} justifyContent={"space-between"}>
                    <div className="input-search" style={{ flex: 1, maxWidth: "50%" }}>
                        <input
                            type="text"
                            className={"form-control"}
                            placeholder={t(
                                `common::label::Please search by folder name`,
                                `Please search by folder name`,
                            )}
                            name="keyword"
                            onChange={(e) => setSearchKeyword(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.code === "Enter") onClickFolderTreeFilter();
                            }}
                            value={searchKeyword}
                            style={{ width: "100%" }}
                        />
                        <i className="sprite sprite-search-dark" />
                        {searchKeyword && (
                            <button
                                type={"button"}
                                className={"btn btn-icon-solid"}
                                onClick={() => {
                                    onClickResetFolderTreeFilter();
                                }}
                            >
                                <i className={"sprite sprite-cancel-source"} />
                            </button>
                        )}
                    </div>
                    <Grid item sx={{ display: "flex", flexDirection: "row", gap: theme.spacing(1) }}>
                        {!disablePopper && (
                            <>
                                <Grid item>
                                    <Tooltip
                                        title={t(`common::label::Create {{name}}`, `Create {{name}}`, {
                                            name: t(`common::label::Folder`, "Folder"),
                                        })}
                                    >
                                        <IconButton
                                            circled
                                            size={"medium"}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                dispatch(
                                                    toggleNewFolderInModal(
                                                        folderTreeViewModal.folderList.selectedFolder.folder,
                                                    ),
                                                );
                                            }}
                                        >
                                            <IconFolderAdd size={16} color={"greySecondary"} />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                                <Divider orientation={"vertical"} flexItem sx={{ marginY: theme.spacing(1) }} />
                            </>
                        )}
                        <Grid item>
                            <Tooltip title={t(`common::label::Refresh`, `Refresh`)}>
                                <IconButton
                                    circled
                                    onClick={() => {
                                        onClickRefreshFolderTreeFilter();
                                    }}
                                >
                                    <IconRefresh size={16} />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
        ),
        [disablePopper, searchKeyword, t, dispatch, folderTreeViewModal.folderList.selectedFolder.folder],
    );

    const FooterComponent = useMemo(
        () =>
            !disablePopper ? (
                <Stack direction={"row"} justifyContent={"flex-end"} p={2}>
                    <Button
                        variant={"contained"}
                        size={"small"}
                        color={"greySecondary"}
                        loading={submitPending}
                        disabled={
                            !checkClickFolder || folderTreeViewModal.folderList.creatingParentFolder || submitPending
                        }
                        onClick={async (e) => {
                            e.preventDefault();
                            onHandleClickSubmit();
                        }}
                    >
                        {submitText ? t(`common::label::${submitText}`) : t(`common::label::${"Select"}`)}
                    </Button>
                </Stack>
            ) : null,
        [
            disablePopper,
            submitPending,
            checkClickFolder,
            folderTreeViewModal.folderList.creatingParentFolder,
            onHandleClickSubmit,
            submitText,
            t,
        ],
    );

    if (open !== undefined && !open) return null;

    // disablePopper가 false일 때는 AdaptivePopper 사용
    if (!disablePopper) {
        return (
            <AdaptivePopper
                open={open}
                anchorEl={anchorEl}
                placement={placement}
                disablePortal={disablePortal}
                wrapperSx={wrapperSx}
                minHeight={150}
                maxHeight={600}
            >
                {HeaderComponent}
                <div data-role="content">
                    <FolderTreeView
                        domain={"Asset"}
                        resourceType={"Asset"}
                        isModal={true}
                        driveList={driveList}
                        folderList={folderList}
                        popperMenu={false}
                        selectedFolder={selectedFolder?.id}
                        onClickForderTree={onClickForderTree}
                        onHandleCreateFolder={handleClickCreateFolder}
                    />
                </div>
                {FooterComponent && <div data-role="footer">{FooterComponent}</div>}
            </AdaptivePopper>
        );
    }

    return (
        <Box
            sx={{
                overflowX: "hidden",
                overflowY: "hidden",
                "& .MuiTreeItem-content": {
                    borderTopRightRadius: "0 !important",
                    borderBottomRightRadius: "0 !important",
                },
                ...wrapperSx,
            }}
        >
            <Grid container flexDirection={"column"} sx={{ height: "inherit" }}>
                <Grid
                    item
                    py={1.5}
                    pr={1.5}
                    pl={0}
                    sx={{ borderBottom: `1px solid ${theme.palette.greySecondary[100]}` }}
                >
                    {HeaderComponent}
                </Grid>
                <Grid
                    item
                    flex={1}
                    className={"tree-view-wrapper"}
                    sx={{ overflowX: "auto", overflowY: "auto", scrollbarGutter: "stable" }}
                >
                    <FolderTreeView
                        domain={"Asset"}
                        resourceType={"Asset"}
                        isModal={true}
                        driveList={driveList}
                        folderList={folderList}
                        popperMenu={false}
                        selectedFolder={selectedFolder?.id}
                        onClickForderTree={onClickForderTree}
                        onHandleCreateFolder={handleClickCreateFolder}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default withTranslation()(WithModal(SelectFolderPopper));
